import { ReactNode, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import AuthenticatedSidebar from "./AuthenticatedSidebar"
import AuthenticatedNavbar from "./AuthenticatedNavbar"
import { OrganizationLoader } from "../components/OrganizationLoader"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { CvUploadProvider } from "@/modules/recruitment/context/CvUploadContext"
import { DocPushProvider } from "@/modules/sage300/context/DocPushContext"
import { BulkRejectionProvider } from "@/modules/recruitment/context/BulkRejectionContext"
import { BulkVideoProvider } from "@/modules/recruitment/context/BulkVideoContext"
import { PluginInstallsProvider } from "@/modules/marketplace/context/PluginInstallsContext"
import { BulkAiCallProvider } from "@/modules/recruitment/context/BulkAiCallContext"

// Routes that do not require an organisation to be selected
const ORG_EXEMPT_PATHS = [
  "/select-organisation",
  "/create-subscription",
  "/multi-tab",
  "/account-suspended",
]

function OrgContextGuard({ children }: { children: ReactNode }) {
  const { organizations, currentOrganization, isLoading } =
    useOrganizationsService()
  const navigate = useNavigate()
  const location = useLocation()

  const isExempt = ORG_EXEMPT_PATHS.some((path) =>
    location.pathname.startsWith(path),
  )

  useEffect(() => {
    if (isLoading || isExempt) return
    if (organizations.length > 0 && !currentOrganization) {
      navigate("/select-organisation", { replace: true })
    }
  }, [isLoading, organizations, currentOrganization, navigate, isExempt])

  return <>{children}</>
}

interface AuthenticatedLayoutProps {
  children: ReactNode
}

export default function AuthenticatedLayout({
  children,
}: AuthenticatedLayoutProps) {
  return (
    <OrganizationLoader>
      <OrgContextGuard>
        <CvUploadProvider>
          <DocPushProvider>
            <BulkRejectionProvider>
              <BulkVideoProvider>
                <PluginInstallsProvider>
                  <BulkAiCallProvider>
                    <div className="flex fixed inset-0 overflow-clip bg-layout-bg">
                      <AuthenticatedSidebar />
                      <div className="flex-1 flex flex-col min-h-0 overflow-clip">
                        <AuthenticatedNavbar />
                        <main className="flex-1 p-3 sm:p-6 overflow-y-auto overflow-x-hidden">
                          {children}
                        </main>
                      </div>
                    </div>
                  </BulkAiCallProvider>
                </PluginInstallsProvider>
              </BulkVideoProvider>
            </BulkRejectionProvider>
          </DocPushProvider>
        </CvUploadProvider>
      </OrgContextGuard>
    </OrganizationLoader>
  )
}
